/**
 * Bookmark popover component with label input and color picker
 * Used in the Dhyana reader for creating/editing bookmarks
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { BookmarkIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';

// Color options for bookmarks
export const BOOKMARK_COLORS = [
  { name: 'blue', class: 'bg-blue-500', text: 'text-blue-500' },
  { name: 'red', class: 'bg-red-500', text: 'text-red-500' },
  { name: 'green', class: 'bg-green-500', text: 'text-green-500' },
  { name: 'yellow', class: 'bg-yellow-500', text: 'text-yellow-500' },
  { name: 'purple', class: 'bg-purple-500', text: 'text-purple-500' },
  { name: 'orange', class: 'bg-orange-500', text: 'text-orange-500' },
];

interface BookmarkPopoverProps {
  pageNumber: number;
  isBookmarked: boolean;
  existingLabel?: string | null;
  existingColor?: string;
  onSave: (label: string | null, color: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  disabled?: boolean;
}

export default function BookmarkPopover({
  pageNumber,
  isBookmarked,
  existingLabel,
  existingColor = 'blue',
  onSave,
  onDelete,
  disabled = false
}: BookmarkPopoverProps) {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [label, setLabel] = useState(existingLabel || '');
  const [color, setColor] = useState(existingColor);
  const [isSaving, setIsSaving] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Reset state when existing values change
  useEffect(() => {
    setLabel(existingLabel || '');
    setColor(existingColor);
  }, [existingLabel, existingColor, pageNumber]);

  // Close popover when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleTogglePopover = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(label.trim() || null, color);
      setIsOpen(false);
    } catch (err) {
      console.error('Failed to save bookmark:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsSaving(true);
    try {
      await onDelete();
      setIsOpen(false);
    } catch (err) {
      console.error('Failed to delete bookmark:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const colorConfig = BOOKMARK_COLORS.find(c => c.name === color) || BOOKMARK_COLORS[0];

  return (
    <div className="relative">
      {/* Bookmark Toggle Button */}
      <button
        ref={buttonRef}
        onClick={handleTogglePopover}
        disabled={disabled}
        className={`p-1.5 rounded-full transition-all ${
          isBookmarked
            ? `${colorConfig.text} hover:opacity-80`
            : 'text-[#1F2937] dark:text-white hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20'
        }`}
        data-tooltip-id="dhyana-tooltip"
        data-tooltip-content={isBookmarked ? 'Edit bookmark' : 'Bookmark this page'}
      >
        {isBookmarked ? (
          <BookmarkSolidIcon className="h-3.5 w-3.5" />
        ) : (
          <BookmarkIcon className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Popover - opens upward since toolbar is at bottom */}
      {isOpen && (
        <div
          ref={popoverRef}
          className={`absolute bottom-full right-0 mb-2 w-72 rounded-xl shadow-xl border z-50 ${
            isDarkMode
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${
            isDarkMode ? 'border-slate-700' : 'border-gray-100'
          }`}>
            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {isBookmarked ? 'Edit Bookmark' : 'Add Bookmark'}
            </span>
            <button
              onClick={() => setIsOpen(false)}
              className={`p-1 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Page info */}
            <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              Page {pageNumber}
            </div>

            {/* Label input */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Label (optional)
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Important diagram..."
                maxLength={255}
                className={`w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors ${
                  isDarkMode
                    ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            {/* Color picker */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Color
              </label>
              <div className="flex gap-2">
                {BOOKMARK_COLORS.map(c => (
                  <button
                    key={c.name}
                    onClick={() => setColor(c.name)}
                    className={`w-7 h-7 rounded-full ${c.class} transition-all ${
                      color === c.name
                        ? 'ring-2 ring-offset-2 ring-cyan-500 scale-110'
                        : 'hover:scale-105'
                    } ${isDarkMode ? 'ring-offset-slate-800' : 'ring-offset-white'}`}
                    title={c.name.charAt(0).toUpperCase() + c.name.slice(1)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className={`flex items-center justify-between px-4 py-3 border-t ${
            isDarkMode ? 'border-slate-700' : 'border-gray-100'
          }`}>
            {isBookmarked && onDelete ? (
              <button
                onClick={handleDelete}
                disabled={isSaving}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors disabled:opacity-50 ${
                  isDarkMode
                    ? 'text-red-400 hover:bg-red-900/30'
                    : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <TrashIcon className="h-4 w-4" />
                Remove
              </button>
            ) : (
              <div />
            )}

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-all"
              style={{
                background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
              }}
            >
              {isSaving ? 'Saving...' : isBookmarked ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
